"use client"

import type React from "react"
import { useState } from "react"
import { useAuth } from "../contexts/AuthContext"
import { AlertCircle } from "lucide-react"

export default function LoginPage() {
  const [username, setUsername] = useState("") // email
  const [otp, setOtp] = useState("")           // OTP input
  const [step, setStep] = useState<"email" | "otp">("email")
  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  const { sendOtp, verifyOtpAndLogin } = useAuth()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")

    if (step === "email") {
      if (!username.trim()) {
        setError("Please insert login.")
        return
      }

      setIsLoading(true)
      const success = await sendOtp(username.trim())
      setIsLoading(false)

      if (success) {
        setStep("otp")
      } else {
        setError("Failed to send One-Time Password. Please check your login.")
      }
    } else if (step === "otp") {
      if (!otp.trim()) {
        setError("Please insert One-Time Password sent on email.")
        return
      }

      setIsLoading(true)
      const success = verifyOtpAndLogin(otp.trim())
      setIsLoading(false)

      if (!success) {
        setError("You have entered an invalid One-Time Password. Please try again.")
      }
    }
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
         <div className="mx-auto h-20 w-auto flex justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="200" height="46" viewBox="0 0 456.5 105" className="h-full">
              <polygon
                points="48.67 21.03 39.48 27.7 39.48 27.7 27.37 36.5 15.31 45.26 8.74 50.03 8.74 40.49 27.45 26.9 48.67 11.49 69.88 26.9 88.59 40.49 88.59 50.03 82.03 45.26 69.97 36.5 57.86 27.7 57.86 27.7 48.67 21.03"
                fill="#004261"
              />
              <path
                d="m17,94.37l6.86-9.37h16.14l-1.66,9.38h-21.34Zm25.49-9.37h12.35l.89,9.38h-14.12l.88-9.38Zm14.82,0h16.16l6.82,9.38h-21.29l-1.69-9.38Zm-31.68-2.45l5.83-8h10.41l-.51,2.94-.9,5.09-14.83-.03Zm17.85-8h10.37l.3,3.18.46,4.85h-11.88l.45-4.85.3-3.18Zm12,0h10.39l5.83,8h-14.82l-.88-5.09-.52-2.94v.03Zm-22.75-1.77l2.83-3.91h7.31l-.69,3.91h-9.45Zm11.27-3.91h9.3l.36,3.91h-10l.34-3.91Zm10.44,0h7.31l2.83,3.91h-9.43l-.71-3.91Z"
                fill="#0cf"
              />
              <g>
                <path
                  d="m276.91,68.56c0,6.34-5.12,11.48-11.46,11.51h-34.26v-5.23h34.26c1.65.02,3.24-.65,4.39-1.84,2.44-2.44,2.44-6.4,0-8.84,0,0,0,0,0,0-1.15-1.19-2.74-1.85-4.39-1.83h-22.8c-3.05.06-5.98-1.14-8.11-3.33-4.47-4.49-4.47-11.74,0-16.23,2.12-2.2,5.06-3.42,8.11-3.37h31.53v5.23h-31.53c-3.45-.14-6.37,2.55-6.51,6s2.55,6.37,6,6.51c.17,0,.33,0,.5,0h22.8c6.32.03,11.43,5.14,11.46,11.46v-.04Zm-51.09,11.51h-34.25c-6.3.03-11.43-5.04-11.46-11.34,0-.04,0-.08,0-.12v-17.79c0-6.32,5.12-11.45,11.44-11.46,0,0,.01,0,.02,0h34.25v5.23h-34.25c-3.43.02-6.21,2.8-6.23,6.23v17.79c.03,3.43,2.8,6.2,6.23,6.23h34.25s0,5.23,0,5.23Zm-61.7-17.26l-8.42-16.12c-.42-.8-1.41-1.12-2.21-.7-.3.16-.54.4-.7.7l-8.6,16.11c-.35.55-.35,1.25,0,1.8.32.57.92.9,1.57.87h16.79c.63.01,1.22-.32,1.54-.87.35-.55.35-1.24,0-1.79h.03Zm15.72,17.26h-6.51c-.31-.54-1.56-2.87-3.73-7-.64-1.43-2.09-2.33-3.66-2.27h-23.45c-1.57-.09-3.04.78-3.72,2.2l-3.6,7h-6.57l21.58-40.71h8.08c.47.85,7.66,14.42,21.58,40.71v.07Z"
                  fill="#002e45"
                />
                <rect x="110.11" y="28.39" width="1.81" height="67.61" fill="#002e45" />
              </g>
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">American Carpet South</h2>
          <p className="mt-2 text-center text-sm text-gray-600">Please sign in to access the portal</p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                Login
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Enter your company email address"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={step === "otp"}
                maxLength={80}
              />
            </div>

            {step === "otp" && (
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
                  One-Time Password
                </label>
                <input
                  id="otp"
                  name="otp"
                  type="text"
                  required
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Enter the One-Time Password sent to your email"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength={6}
                />
              </div>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Processing..." : step === "email" ? "Send One-Time Password" : "Verify One-Time Password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
